import React, { useState, useEffect, useMemo } from "react";
import AuthenticatedTemplate from "../../components/AuthenticatedTemplate";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

import { useOrganisationsStore } from "../../stores/organisations-store";
import { LOGOS, ICONS } from "../../utils/assets";
import { Footer } from "../../components/Footer";
import Button from "../../components/form/Button";
import ProgressBar from "../../components/ProgressBar";
import { SelectedOrganisationsCard } from "../../components/SelectedOrganisationsCard";
import useDeviceSize from "../../hooks/useDeviceSize";

function Section(props) {
  const [isOpen, setOpen] = useState(false);
  const toggle = () => {
    setOpen(!isOpen);
  };
  return (
    <div className="expandable-organisation">
      <div className="expandable-organisation__header" onClick={toggle}>
        <div className="paragraph-md semibold paragraph-md--dark">
          {props.title}
        </div>
        <i
          className={"caret-icon fas fa-caret-" + (isOpen ? "up" : "down")}
        ></i>
      </div>
      {isOpen && (
        <div className="expandable-organisation__content">{props.children}</div>
      )}
    </div>
  );
}

export const Dashboard04Organisations = () => {
  const navigate = useNavigate();
  const [deviceWidth] = useDeviceSize();
  //Const to get the value typed from "Search Input"
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  //const [path, setPath] = useState("/dashboard-identity");
  const [path, setPath] = useState("/dashboard-update-details");
  const [errors, setErrors] = useState({});

  const { selectedOrganisations, selectOrganisation, removeOrganisation } =
    useOrganisationsStore();

  useEffect(() => {
    const LS_SKIP_IDENTITY = sessionStorage.getItem("LS_SKIP_IDENTITY");
    if (LS_SKIP_IDENTITY) {
      setPath("/dashboard-update-details");
    }

    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL}/organisation/get-organisation-data`
      );
      const categories = res.data;

      // Check if the selected organisations exist in the categories
      // and set isAdded to true if it does
      const updatedCategories = categories.map((category) => ({
        ...category,
        items: category.items.map((item) => ({
          ...item,
          isAdded: selectedOrganisations.some(
            (selectedOrg) =>
              selectedOrg.Organisation_ID === item.Organisation_ID
          ),
        })),
      }));

      if (updatedCategories.length > 0) {
        setCategories(updatedCategories);
        setOnlyOrganisations(updatedCategories);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error(
        "Error fetching organisation data:",
        error?.response?.data || error.message
      );
    }
  };

  const setOnlyOrganisations = (categories) => {
    categories.forEach((category) => {
      if (category.items.length === 0) return;

      category.items.forEach((item) => {
        // Validation to avoid adding the same organisation twice
        setOrganisations((prev) => {
          const duplicatedOrganisation = prev.some(
            (org) => org.Organisation_ID === item.Organisation_ID
          );

          if (!duplicatedOrganisation) {
            return [...prev, item];
          }

          return prev;
        });
      });
    });
  };

  const clearInputError = (name, value) => {
    const errorKey = name;

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (value !== "") {
        delete newErrors[errorKey];
      }
      return newErrors;
    });
  };

  const handleAddOrganisation = (selectedOrg) => {
    setQuery(""); // Clear the input field
    const selectedOrgId = selectedOrg.Organisation_ID;

    const updatedCategories = categories.map((category) => ({
      ...category,
      items: category.items.map((item) => {
        return item.Organisation_ID === selectedOrgId
          ? { ...item, isAdded: true }
          : item;
      }),
    }));
    setCategories(updatedCategories);

    const updatedOrganisations = organisations.map((org) => {
      return org.Organisation_ID === selectedOrgId
        ? { ...org, isAdded: true }
        : org;
    });
    setOrganisations(updatedOrganisations);

    selectOrganisation(selectedOrg);

    clearInputError("selectedOrganisations", selectedOrgId);
  };

  const handleRemoveOrganisation = (orgId) => {
    const updatedCategories = categories.map((category) => ({
      ...category,
      items: category.items.map((item) => {
        return item.Organisation_ID === orgId
          ? { ...item, isAdded: false }
          : item;
      }),
    }));
    setCategories(updatedCategories);

    const updatedOrganisations = organisations.map((org) => {
      return org.Organisation_ID === orgId ? { ...org, isAdded: false } : org;
    });
    setOrganisations(updatedOrganisations);

    removeOrganisation(orgId);
  };

  /**
   * Function to filter organisations from "organisations" that received data
   * "setOrganisations" function
   */
  const setFilteredItems = useMemo(() => {
    return organisations.filter((item) => {
      return item.Organisation_Name.toLowerCase().includes(query.toLowerCase());
    });
  }, [query, organisations]);

  const handleContinue = () => {
    const isValid = validateFields();

    if (!isValid) return;

    const allAccountLabelsNull = selectedOrganisations.every(
      (item) => !item.Organisation_Account_Label
    );

    if (allAccountLabelsNull) {
      navigate("/dashboard-review-details");
    } else {
      navigate("/dashboard-organisations-details");
    }
  };

  const validateFields = () => {
    let isValid = true;
    let newErrors = {};

    if (!selectedOrganisations.length) {
      newErrors["selectedOrganisations"] =
        "Please select at least one organisation.";
      isValid = false;
    }

    setErrors(newErrors);

    return isValid;
  };

  return (
    <>
      <AuthenticatedTemplate>
        <section className="step__container organisation">
          <div className="step__content">
            <div className="step-organisation">
              <div className="step__header">
                <img
                  className="logo"
                  src={LOGOS.QUICKMOVE_FLOW}
                  alt="Quickmove logo"
                />
                <div>
                  <h1 className="text-md text-md--variant">
                    Choose your organisations
                  </h1>
                  <p className="paragraph-md">
                    Select the providers you want to update your details with.
                  </p>
                </div>
              </div>
              <ProgressBar percentage="42" />

              {selectedOrganisations.length && deviceWidth <= 768 ? (
                <SelectedOrganisationsCard
                  selectedOrganisations={selectedOrganisations}
                  onDeleteOrganisation={handleRemoveOrganisation}
                />
              ) : null}

              <div className="input-field">
                <label htmlFor="organisation">Search Organisations</label>
                <div className="input-field__wrapper">
                  <input
                    id="organisation"
                    className="w-100"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    type="text"
                    placeholder="Enter your organization name"
                  ></input>
                  <i className="fa fa-search input-field__icon"></i>
                </div>
              </div>
              {/* {loadingOrganisations && (
              <p className="paragraph-md text-center">Loading...</p>
            )} */}
              <div className="organisation-options">
                {/*"Section for the organisation search"*/}
                {query
                  ? setFilteredItems.map((item, index) => {
                      return (
                        <div className="organisation-category" key={index}>
                          <div className="organisation-category__info">
                            <div className="logo-box">
                              <img
                                className="logo-box__image"
                                src={item.Organisation_Logo_Path}
                                alt={`${item.Organisation_Name} logo`}
                              />
                            </div>
                            <p className="paragraph-md semibold paragraph-md--dark">
                              {item.Organisation_Name}
                            </p>
                          </div>
                          <Button
                            variant="secondary"
                            compact
                            onClick={() => handleAddOrganisation(item)}
                          >
                            {item.isAdded ? "Added" : "Add"}
                          </Button>
                        </div>
                      );
                    })
                  : null}

                {categories ? (
                  categories.map((category, index) => (
                    <Section
                      style={{
                        color: "#120F2A",
                        fontSize: "14px",
                        fontWeight: "400",
                      }}
                      title={category.category_name}
                      key={index}
                    >
                      {category.items.length ? (
                        category.items.map((item, index) => {
                          return (
                            <div
                              className="organisation-category"
                              key={index + 200}
                            >
                              <div className="organisation-category__info">
                                <div className="logo-box">
                                  <img
                                    className="logo-box__image"
                                    src={item.Organisation_Logo_Path}
                                    alt={` ${item.Organisation_Name} logo`}
                                  />
                                </div>
                                <p className="paragraph-md semibold paragraph-md--dark">
                                  {item.Organisation_Name}
                                </p>
                              </div>
                              <Button
                                variant="secondary"
                                compact
                                onClick={() => handleAddOrganisation(item)}
                              >
                                {item.isAdded ? "Added" : "Add"}
                              </Button>
                            </div>
                          );
                        })
                      ) : (
                        <p className="paragraph-md text-center">
                          No organisations found.
                        </p>
                      )}
                    </Section>
                  ))
                ) : (
                  <p className="paragraph-md text-center">
                    No organisations found.
                  </p>
                )}
                {errors.selectedOrganisations && (
                  <p className="paragraph-md text-danger">
                    {errors.selectedOrganisations}
                  </p>
                )}
              </div>
              <div className="action-buttons">
                <Link to={path}>
                  <Button variant="outlined">Back</Button>
                </Link>
                <Button
                  variant="primary"
                  icon={ICONS.CARET_ICON}
                  iconAlt="Caret icon"
                  onClick={() => handleContinue()}
                >
                  Continue
                </Button>
              </div>
              <Footer />
            </div>
          </div>

          {selectedOrganisations.length && deviceWidth > 768 ? (
            <SelectedOrganisationsCard
              selectedOrganisations={selectedOrganisations}
              onDeleteOrganisation={handleRemoveOrganisation}
            />
          ) : null}
        </section>
      </AuthenticatedTemplate>
    </>
  );
};
